// /auth/callback - landing target for OAuth providers.
//
// Supabase defaults to the PKCE flow, which returns ?code=... in the query
// string and requires an explicit exchangeCodeForSession() call. (The older
// implicit flow returned #access_token in the hash and was auto-parsed by
// detectSessionInUrl - but PKCE is not.) We exchange the code here, then
// redirect to '/' once the session is established.
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase.js';

export default function AuthCallback() {
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = new URL(window.location.href);
        const hasCode = url.searchParams.get('code');
        const errParam = url.searchParams.get('error_description') || url.searchParams.get('error');

        if (errParam) {
          if (!cancelled) setErr(errParam);
          return;
        }

        if (hasCode) {
          // PKCE: turn the ?code into a session (uses the stored code verifier).
          const { error } = await supabase.auth.exchangeCodeForSession(window.location.href);
          if (error) {
            if (!cancelled) setErr(error.message);
            return;
          }
        } else {
          // Fallback: implicit/hash flow - give detectSessionInUrl a moment.
          await supabase.auth.getSession();
        }

        // Session is now established; AuthProvider's onAuthStateChange will fire.
        if (!cancelled) {
          // If sign-in began from an invite, return there so the invitation
          // is consumed; otherwise land on home.
          // Prefer the stashed invite path (set by LoginPage before the clean OAuth
          // round-trip). Fall back to a ?next= param for any older links in flight.
          let next = null;
          try { next = window.localStorage.getItem('g360_post_login_next'); } catch (e) {}
          if (next) { try { window.localStorage.removeItem('g360_post_login_next'); } catch (e) {} }
          if (!next) next = url.searchParams.get('next');
          const dest = next && next.startsWith('/') ? next : '/';
          window.history.replaceState({}, '', dest);
          window.dispatchEvent(new PopStateEvent('popstate'));
        }
      } catch (e) {
        if (!cancelled) setErr(String(e?.message || e));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--g-bg)]">
      <div className="text-center">
        {err ? (
          <>
            <p className="text-sm" style={{ color: 'var(--g-danger)' }}>Sign-in failed: {err}</p>
            <a href="/" className="mt-4 inline-block text-sm" style={{ color: 'var(--g-primary)' }}>Back to sign in</a>
          </>
        ) : (
          <>
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-[var(--g-primary)] border-t-transparent" />
            <p className="mt-4 text-sm text-[var(--g-text-2)]">Signing you in...</p>
          </>
        )}
      </div>
    </div>
  );
}
